<template>
    <div class="wrapper">
        <div class="wrapper-header">
            <TabsWidget @tab="switchTab" activeTab="uploaded" />
            <div class="tab-options">
                <div class="filter-options">
                    <div class="status-filter">
                        <b-field>
                            <b-select v-model="statusFilter" placeholder="Filter by status">
                                <option value="all">All Status</option>
                                <option value="completed">Completed</option>
                                <option value="uploading">Processing</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="search-field">
                        <b-field>
                            <b-input v-model="searchQuery" placeholder="Search uploads..." type="search"
                                icon="magnify"></b-input>
                        </b-field>
                    </div>
                </div>
                <b-button type="is-primary" @click="toggleModal = true" icon-left="upload">ADD NEW</b-button>
            </div>
        </div>


        <div class="content-container" v-if="!loading && filteredItems.length > 0">

            <div class="upload-stats" v-if="!loading && uploadedItems.length > 0">
                <div class="stat-card">
                    <div class="stat-icon">
                        <b-icon icon="file-upload" size="is-medium"></b-icon>
                    </div>
                    <div class="stat-info">
                        <span class="stat-value">{{ stats.totalUploads }}</span>
                        <span class="stat-label">Total Uploads</span>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon completed">
                        <b-icon icon="check-circle" size="is-medium"></b-icon>
                    </div>
                    <div class="stat-info">
                        <span class="stat-value">{{ stats.completedUploads }}</span>
                        <span class="stat-label">Completed</span>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon in-progress">
                        <b-icon icon="progress-upload" size="is-medium"></b-icon>
                    </div>
                    <div class="stat-info">
                        <span class="stat-value">{{ stats.inProgressUploads }}</span>
                        <span class="stat-label">In Progress</span>
                    </div>
                </div>
            </div>

            <div class="wrapper-body" :class="{ activeSide: $store.state.sidebar }">
                <uploadedCard @deleteMedia="deleteMedia(item)" @playMedia="playMedia(item)" :canDelete="true"
                    v-for="(item, index) in filteredItems" :key="item.id" :dataObj='item' />
            </div>
            <div class="pagination-container">
                <b-pagination v-model="pagination.currentPage" :total="pagination.total" :per-page="pagination.perPage"
                    @change="handlePageChange" order="is-centered" aria-next-label="Next page"
                    aria-previous-label="Previous page" aria-page-label="Page" aria-current-label="Current page">
                </b-pagination>
            </div>
        </div>
        <div v-else-if="!loading && uploadedItems.length > 0 && filteredItems.length === 0" class="no-results">
            <div class="no-results-content">
                <b-icon icon="file-search" size="is-large"></b-icon>
                <h3>No matching uploads found</h3>
                <p>Try adjusting your search or filter criteria</p>
                <b-button @click="clearFilters" type="is-text">Clear filters</b-button>
            </div>
        </div>
        <div class="no-results" v-else>
            <widgetNotfound v-if="loading" title="Please Wait..." subtitle="Loading Data..."></widgetNotfound>
            <widgetNotfound v-else title="No Uploads Found" subtitle="Upload new files to get started">
                <template #action>
                    <b-button type="is-primary" @click="toggleModal = true" icon-left="upload" class="mt-4">
                        Upload Now
                    </b-button>
                </template>
            </widgetNotfound>
        </div>

        <AddNewUpload v-if="toggleModal" @closeModal="toggleModal = false" @refresh="fetchUploads" />


        <!-- ClapprPlayer -->
        <b-modal v-model="show_player" :width="800">
            <div style="height: 500px" v-if="selected_media">
                <ClapprPlayer :src="`${$store.state.ASSETS_VIDEO_BASE_URL}${selected_media.videoPath}`" type="hls">
                </ClapprPlayer>
            </div>
        </b-modal>
    </div>
</template>

<script>
import TabsWidget from './components/TabsWidget.vue';
import uploadedCard from './components/uploadedCard.vue';
import AddNewUpload from './modals/addNewUpload.vue';
import { GET_STREAMER_MEDIA, DELETE_STREAMER_MEDIA } from '@/store/actions/audvisual';

export default {
    data() {
        return {
            toggleModal: false,
            loading: false,
            statusFilter: 'completed',
            searchQuery: '',
            uploadedItems: [],
            pagination: {
                currentPage: 1,
                perPage: 12,
                total: 0
            },
            stats: {
                totalUploads: 0,
                completedUploads: 0,
                inProgressUploads: 0
            },
            error: null,
            show_player: false,
            selected_media: null
        }
    },
    components: { uploadedCard, AddNewUpload, TabsWidget },
    computed: {
        filteredItems() {
            return this.uploadedItems;
        }
    },
    methods: {
        playMedia(item) {
            let vm = this;
            vm.show_player = true;
            vm.selected_media = item;
        },
        switchTab(type) {
            let vm = this;
            vm.$emit("tab", type);
        },
        fetchUploads() {
            let vm = this;
            vm.loading = true;
            vm.error = null;

            // Construct query parameters
            const params = {
                page: vm.pagination.currentPage,
                per_page: vm.pagination.perPage,
                status: vm.statusFilter !== 'all' ? vm.statusFilter : undefined,
                search: vm.searchQuery || undefined
            };

            // Make API call using Vuex action
            vm.$store.dispatch(GET_STREAMER_MEDIA, params)
                .then((response) => {
                    if (response.data) {
                        // Update local state
                        vm.uploadedItems = response.data.items;
                        vm.pagination.total = response.data.total;
                        vm.stats = {
                            totalUploads: response.data.stats.total,
                            completedUploads: response.data.stats.completedUploads,
                            inProgressUploads: response.data.stats.inProgressUploads
                        };
                    }
                })
                .catch((error) => {
                    vm.error = 'Failed to fetch uploads. Please try again.';
                    vm.$notify({
                        group: "status",
                        type: "error",
                        title: "Error",
                        text: vm.error,
                    });
                })
                .finally(() => {
                    vm.loading = false;
                });
        },
        handlePageChange(page) {
            let vm = this;
            vm.pagination.currentPage = page;
            vm.fetchUploads();
        },
        handleFiltersChange() {
            let vm = this;
            vm.pagination.currentPage = 1; // Reset to first page
            vm.fetchUploads();
        },
        deleteMedia(item) {
            let vm = this;
            vm.$buefy.dialog.confirm({
                title: 'Deleting Media',
                message: `Are you sure you want to delete this uploaded file?`,
                confirmText: 'Delete',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: () => {
                    vm.$store.dispatch(DELETE_STREAMER_MEDIA, { id: item.id })
                        .then(() => {
                            vm.fetchUploads(); // Refresh the list
                            vm.$notify({
                                group: "status",
                                type: "success",
                                title: "SUCCESS!!!",
                                text: 'File Deleted Successfully',
                            });
                        })
                        .catch(() => {
                            vm.$notify({
                                group: "status",
                                type: "error",
                                title: "Error",
                                text: 'Failed to delete file. Please try again.',
                            });
                        });
                }
            })
        },
        clearFilters() {
            let vm = this;
            vm.statusFilter = 'all';
            vm.searchQuery = '';
            vm.handleFiltersChange();
        }
    },
    watch: {
        statusFilter: 'handleFiltersChange',
        searchQuery: {
            handler: 'handleFiltersChange',
            debounce: 300
        }
    },
    created() {
        this.fetchUploads();
    }
};
</script>

<style lang="scss" scoped>
.wrapper {
    padding: 0px;
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: #f9f9f9;

    .wrapper-header {
        width: 100%;
        height: 56px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #eee;
        padding-left: 6px;
        overflow-x: auto;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        .tab-options {
            display: flex;
            align-items: center;
            padding: 0px 10px;
            gap: 15px;

            .filter-options {
                display: flex;
                gap: 10px;

                @media (max-width: 768px) {
                    display: none;
                }

                .status-filter {
                    @media (max-width: 768px) {
                        display: none;
                    }
                }

                .search-field {
                    width: 200px;
                }
            }
        }
    }



    .content-container {
        height: calc(100% - 56px);
        display: flex;
        flex-direction: column;

        .upload-stats {
            height: 140px;
            display: flex;
            padding: 20px;
            gap: 20px;
            flex-wrap: wrap;

            @media (max-width: 768px) {
                padding: 15px;
                gap: 10px;
            }

            .stat-card {
                background: white;
                border-radius: 10px;
                padding: 15px;
                display: flex;
                align-items: center;
                gap: 15px;
                flex: 1;
                min-width: 200px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

                @media (max-width: 768px) {
                    min-width: calc(50% - 10px);
                    padding: 12px;
                }

                @media (max-width: 480px) {
                    min-width: 100%;
                }

                .stat-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 10px;
                    background: #f0f0f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #666;

                    &.completed {
                        background: rgba(72, 199, 116, 0.1);
                        color: #48c774;
                    }

                    &.in-progress {
                        background: rgba(50, 152, 220, 0.1);
                        color: #3298dc;
                    }
                }

                .stat-info {
                    display: flex;
                    flex-direction: column;

                    .stat-value {
                        font-size: 24px;
                        font-weight: 600;
                        color: #333;
                    }

                    .stat-label {
                        font-size: 14px;
                        color: #666;
                    }
                }
            }
        }

        .wrapper-body {
            padding: 0 20px 20px;
            overflow-y: auto;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 25px;
            align-items: stretch;
            /* Ensure all items stretch to fill their grid cell */

            @media (max-width: 768px) {
                gap: 15px;
            }

            @media (max-width: 480px) {
                grid-template-columns: 1fr;
            }
        }


    }


    .pagination-container {
        margin-top: 10px;
        padding: 10px 0;
        display: flex;
        justify-content: center;

        @media (max-width: 768px) {
            margin-top: 20px;
            padding: 15px 0;
        }
    }

    .no-results {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 50px 20px;

        .no-results-content {
            text-align: center;

            .b-icon {
                color: #ccc;
                margin-bottom: 15px;
            }

            h3 {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 5px;
                color: #333;
            }

            p {
                color: #666;
                margin-bottom: 15px;
            }
        }
    }
}
</style>
